import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, authFetch } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { DashboardTour } from '../components/DashboardTour';
import { toast } from 'sonner';
import {
  MessageSquare,
  Briefcase,
  Calculator,
  BarChart3,
  DollarSign,
  Radar,
  FileText,
  Users,
  Lightbulb,
  ArrowRight,
  Clock,
  Sparkles,
  Loader2,
  CheckCircle2,
  Send,
  Gift,
  PiggyBank,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const activeTools = [
  {
    href: '/advisor',
    icon: MessageSquare,
    title: 'Asesor Laboral IA',
    description: 'Obtén orientación sobre situaciones laborales con IA',
    color: 'bg-blue-500',
  },
  {
    href: '/job-evaluator',
    icon: Briefcase,
    title: 'Evaluador de Oferta',
    description: 'Analiza y compara ofertas de trabajo',
    color: 'bg-emerald-500',
  },
  {
    href: '/severance',
    icon: Calculator,
    title: 'Calculadora de Liquidación',
    description: 'Estima tu liquidación y finiquito',
    color: 'bg-amber-500',
  },
  {
    href: '/salary-benchmark',
    icon: BarChart3,
    title: 'Benchmark Salarial',
    description: 'Compara tu salario con el mercado',
    color: 'bg-purple-500',
  },
  {
    href: '/net-salary',
    icon: DollarSign,
    title: 'Calculadora de Sueldo Neto',
    description: 'Descubre cuánto recibes después de impuestos y IMSS',
    color: 'bg-teal-500',
  },
  {
    href: '/retiro',
    icon: PiggyBank,
    title: 'Planeación de Retiro',
    description: 'Revisa tus semanas cotizadas y qué ley de pensión te aplica',
    color: 'bg-indigo-500',
  },
];

const comingSoonTools = [
  { icon: Radar, title: 'Radar de Carrera', description: 'Descubre habilidades y movimientos para crecer' },
  { icon: Lightbulb, title: 'Tu idea', description: 'Sugiere la herramienta que necesitas' },
  { icon: FileText, title: 'Optimizador de CV y LinkedIn', description: 'Optimiza tu CV y perfil de LinkedIn' },
  { icon: Users, title: 'Bolsa de Trabajo', description: 'Encuentra oportunidades laborales' },
];

export default function DashboardPage() {
  const { user, updateUser } = useAuth();
  const [recentActivity, setRecentActivity] = useState(null);
  const [registeredInterests, setRegisteredInterests] = useState([]);
  const [ideaText, setIdeaText] = useState('');
  const [submittingIdea, setSubmittingIdea] = useState(false);
  const [ideaSubmitted, setIdeaSubmitted] = useState(false);
  const [referralStats, setReferralStats] = useState(null);
  const [showTourPrompt, setShowTourPrompt] = useState(false);
  const [tourActive, setTourActive] = useState(false);

  useEffect(() => {
    fetchRecentActivity();
    if (user?.referral_code) fetchReferralStats();
  }, [user]);

  useEffect(() => {
    if (user && user.dashboard_tour_seen === false) {
      setShowTourPrompt(true);
    }
  }, [user]);

  // Actualiza el estado local de inmediato (para que el popup nunca se vuelva a mostrar
  // en esta misma sesión aunque la llamada al servidor tarde o falle) y en paralelo
  // persiste el flag en el backend, siempre sobre el propio usuario autenticado.
  const markTourSeen = () => {
    updateUser({ ...user, dashboard_tour_seen: true });
    authFetch(`${API_URL}/api/user/dashboard-tour-seen`, { method: 'POST' }).catch((error) => {
      console.error('Error marking dashboard tour as seen:', error);
    });
  };

  const closeTourPrompt = () => {
    setShowTourPrompt(false);
    markTourSeen();
  };

  const handleStartTour = () => {
    closeTourPrompt();
    setTourActive(true);
  };

  const fetchReferralStats = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/referral/stats`);
      if (response.ok) setReferralStats(await response.json());
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/saved-results`);
      if (response.ok) {
        const data = await response.json();
        setRecentActivity(data);
      }
    } catch (error) {
      console.error('Error fetching activity:', error);
    }
  };

  const handleInterest = async (featureName) => {
    try {
      await authFetch(`${API_URL}/api/features/interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feature_name: featureName }),
      });
      setRegisteredInterests((prev) => [...prev, featureName]);
      toast.success('¡Interés registrado!');
    } catch (error) {
      console.error('Error registering interest:', error);
    }
  };

  const handleSubmitIdea = async () => {
    if (!ideaText.trim()) return;
    setSubmittingIdea(true);
    try {
      const response = await authFetch(`${API_URL}/api/features/idea`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idea_text: ideaText }),
      });
      if (response.ok) {
        setIdeaSubmitted(true);
        toast.success('¡Gracias por tu idea! La revisaremos.');
      }
    } catch (error) {
      toast.error('Error al enviar tu idea');
    } finally {
      setSubmittingIdea(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '¡Buenos días';
    if (hour < 18) return '¡Buenas tardes';
    return '¡Buenas noches';
  };

  const getActivityCount = () => {
    if (!recentActivity) return 0;
    return (
      (recentActivity.chat_sessions?.length || 0) +
      (recentActivity.job_evaluations?.length || 0) +
      (recentActivity.severance_calculations?.length || 0) +
      (recentActivity.salary_benchmarks?.length || 0)
    );
  };

  return (
    <Layout>
      <Dialog open={showTourPrompt} onOpenChange={(open) => { if (!open) closeTourPrompt(); }}>
        <DialogContent className="sm:max-w-sm" data-testid="dashboard-tour-prompt">
          <DialogHeader>
            <DialogTitle>¿Quieres un recorrido rápido?</DialogTitle>
            <DialogDescription>
              Te mostramos en 6 pasos qué puedes hacer con Klarvi. Toma menos de un minuto.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={closeTourPrompt} data-testid="dashboard-tour-prompt-no">
              Ahora no
            </Button>
            <Button
              onClick={handleStartTour}
              className="bg-[#2563EB] hover:bg-[#2563EB]/90"
              data-testid="dashboard-tour-prompt-yes"
            >
              Sí, muéstrame
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <DashboardTour active={tourActive} onClose={() => setTourActive(false)} />
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Welcome Section */}
        <div className="animate-fade-in">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] font-['Manrope']">
            {getGreeting()}, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="mt-2 text-muted-foreground text-lg">
            ¿En qué te podemos ayudar hoy?
          </p>
        </div>

        {/* Active Tools Grid */}
        <section>
          <h2 className="text-xl font-semibold text-[#0F172A] font-['Manrope'] mb-4">
            Herramientas Activas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {activeTools.map((tool, index) => {
              const Icon = tool.icon;
              return (
                <Link
                  key={tool.href}
                  to={tool.href}
                  data-testid={`tool-${tool.href.slice(1)}`}
                  className="group"
                >
                  <Card className="h-full border hover:shadow-lg transition-all duration-200 hover:-translate-y-1 hover:border-[#2563EB]/30">
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-xl ${tool.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-semibold text-[#0F172A] font-['Manrope'] group-hover:text-[#2563EB] transition-colors">
                        {tool.title}
                      </h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {tool.description}
                      </p>
                      <div className="mt-4 flex items-center text-sm text-[#2563EB] opacity-0 group-hover:opacity-100 transition-opacity">
                        Abrir
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Referral Card */}
        <section className="animate-slide-up" style={{ animationDelay: '50ms' }}>
          <Card className="border-0 bg-gradient-to-r from-teal-600 to-teal-700 overflow-hidden" data-testid="dashboard-referral-card">
            <CardContent className="p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-white font-['Manrope']">
                  Invita amigos y gana descuentos
                </h3>
                <p className="mt-1 text-teal-100 text-sm leading-relaxed">
                  Si se hacen Pro, tú ganas: un mes gratis por cada plan anual o 10% de descuento por cada plan mensual activo. Se acumulan hasta que tu Pro quede gratis.
                </p>
                {referralStats && (
                  <div className="mt-3 flex flex-wrap items-center gap-4" data-testid="dashboard-referral-stats">
                    <div>
                      <span className="text-2xl font-bold text-white" data-testid="dashboard-referral-discount">
                        {referralStats.monthly_discount_pct}%
                      </span>
                      <span className="ml-1.5 text-xs text-teal-100">descuento actual</span>
                    </div>
                    <div>
                      <span className="text-2xl font-bold text-white" data-testid="dashboard-referral-active-count">
                        {referralStats.monthly_active + referralStats.annual_active}
                      </span>
                      <span className="ml-1.5 text-xs text-teal-100">referidos activos</span>
                    </div>
                  </div>
                )}
              </div>
              <Link to="/profile#referral-section" className="flex-shrink-0">
                <Button
                  className="bg-white text-teal-700 hover:bg-white/90 font-semibold"
                  data-testid="dashboard-referral-cta"
                >
                  Ver mi link
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </section>

        {/* Recent Activity */}
        {getActivityCount() > 0 && (
          <section className="animate-slide-up" style={{ animationDelay: '100ms' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-[#0F172A] font-['Manrope']">
                Actividad Reciente
              </h2>
              <Link
                to="/saved"
                className="text-sm text-[#2563EB] hover:underline flex items-center gap-1"
                data-testid="view-all-saved"
              >
                Ver todo
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <Card className="border">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3">
                  {recentActivity?.chat_sessions?.slice(0, 2).map((session) => (
                    <Link
                      key={session.session_id}
                      to={`/advisor?session=${session.session_id}`}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors text-sm"
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span className="truncate max-w-[150px]">{session.title}</span>
                    </Link>
                  ))}
                  {recentActivity?.job_evaluations?.slice(0, 2).map((eval_) => (
                    <div
                      key={eval_.evaluation_id}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm"
                    >
                      <Briefcase className="w-4 h-4" />
                      <span className="truncate max-w-[150px]">{eval_.input_data?.role}</span>
                    </div>
                  ))}
                  {recentActivity?.salary_benchmarks?.slice(0, 2).map((bench) => (
                    <div
                      key={bench.benchmark_id}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-50 text-purple-700 text-sm"
                    >
                      <BarChart3 className="w-4 h-4" />
                      <span className="truncate max-w-[150px]">{bench.input_data?.role}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Coming Soon Section */}
        <section className="animate-slide-up" style={{ animationDelay: '200ms' }}>
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-xl font-semibold text-[#0F172A] font-['Manrope']">
              Próximamente
            </h2>
            <Badge variant="secondary" className="text-xs">
              <Sparkles className="w-3 h-3 mr-1" />
              En desarrollo
            </Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {comingSoonTools.map((tool, index) => {
              const Icon = tool.icon;
              const isIdea = tool.title === 'Tu idea';
              const isInterested = registeredInterests.includes(tool.title);

              return (
                <Card
                  key={tool.title}
                  className="border bg-muted/30 opacity-80"
                  data-testid={`dashboard-coming-${tool.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <h3 className="font-semibold text-muted-foreground font-['Manrope']">
                      {tool.title}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground/70 flex-1">
                      {tool.description}
                    </p>

                    {isIdea ? (
                      ideaSubmitted ? (
                        <div className="mt-4 flex items-center gap-2 text-sm text-[#10B981] font-medium">
                          <CheckCircle2 className="w-4 h-4" />
                          ¡Idea enviada!
                        </div>
                      ) : (
                        <div className="mt-4 space-y-2">
                          <Textarea
                            placeholder="Describe tu idea..."
                            value={ideaText}
                            onChange={(e) => setIdeaText(e.target.value)}
                            className="resize-none text-sm min-h-[60px]"
                            maxLength={500}
                            data-testid="dashboard-idea-textarea"
                          />
                          <Button
                            size="sm"
                            className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90"
                            onClick={handleSubmitIdea}
                            disabled={!ideaText.trim() || submittingIdea}
                            data-testid="dashboard-idea-submit-btn"
                          >
                            {submittingIdea ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                            Enviar idea
                          </Button>
                        </div>
                      )
                    ) : (
                      <Button
                        variant={isInterested ? 'secondary' : 'outline'}
                        size="sm"
                        className="mt-4 w-full"
                        onClick={() => handleInterest(tool.title)}
                        disabled={isInterested}
                        data-testid={`interest-${tool.title.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {isInterested ? 'Interés registrado' : 'Me interesa'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Quick Help */}
        <section className="animate-slide-up" style={{ animationDelay: '300ms' }}>
          <Card className="border bg-gradient-to-r from-[#0F172A] to-[#1E293B]">
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="text-white">
                  <h3 className="text-xl font-semibold font-['Manrope']">
                    ¿No sabes por dónde empezar?
                  </h3>
                  <p className="mt-1 text-slate-300">
                    Visita nuestra guía para entender cómo Klarvi puede ayudarte.
                  </p>
                </div>
                <Link to="/guidance">
                  <Button
                    className="bg-white text-[#0F172A] hover:bg-white/90"
                    data-testid="guidance-cta"
                  >
                    ¿Qué hago?
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </Layout>
  );
}
